import { useState, useEffect, useMemo } from 'react';

const TIME_FORMAT_HINT = 'Accepted formats: 8:00 AM, 2:30 PM, 14:30, or full datetime like 2026-03-15 8:00 AM';

/**
 * LacSched fields available for mapping, grouped by category.
 */
const FIELD_GROUPS = [
  {
    label: 'Session Fields',
    fields: [
      { value: 'title', label: 'Title', required: true },
      { value: 'description', label: 'Description' },
      { value: 'session_type', label: 'Session Type' },
      { value: 'start_time', label: 'Start Time', hint: TIME_FORMAT_HINT },
      { value: 'end_time', label: 'End Time', hint: TIME_FORMAT_HINT },
      { value: 'location', label: 'Location' },
      { value: 'room', label: 'Room' },
      { value: 'track', label: 'Licensure' },
      { value: 'audience', label: 'Audience' },
      { value: 'credit_hours', label: 'Clock Hours' },
      { value: 'capacity', label: 'Capacity' },
    ],
  },
  {
    label: 'Speaker Fields',
    fields: [
      { value: 'speaker_name', label: 'Speaker Name' },
      { value: 'speaker_bio', label: 'Speaker Bio' },
      { value: 'speaker_title', label: 'Speaker Title' },
      { value: 'speaker_company', label: 'Speaker Company' },
      { value: 'speaker_email', label: 'Speaker Email' },
    ],
  },
];

/**
 * Auto-detection rules: lowercase header patterns -> field name.
 */
const AUTO_DETECT_RULES = [
  // Session fields
  { patterns: ['title', 'session title', 'name', 'session name'], field: 'title' },
  { patterns: ['description', 'abstract', 'summary'], field: 'description' },
  { patterns: ['type', 'session type', 'session_type'], field: 'session_type' },
  { patterns: ['start', 'start time', 'start_time', 'begins', 'begin time'], field: 'start_time' },
  { patterns: ['end', 'end time', 'end_time', 'ends', 'end time'], field: 'end_time' },
  { patterns: ['location', 'venue'], field: 'location' },
  { patterns: ['room'], field: 'room' },
  { patterns: ['track', 'licensure', 'category', 'strand'], field: 'track' },
  { patterns: ['audience', 'audience tag', 'audience type'], field: 'audience' },
  { patterns: ['clock hours', 'credit hours', 'hours', 'ce hours', 'credits'], field: 'credit_hours' },
  { patterns: ['capacity', 'max', 'seats'], field: 'capacity' },
  // Speaker fields
  { patterns: ['speaker', 'speaker name', 'presenter', 'presenter name'], field: 'speaker_name' },
  { patterns: ['bio', 'speaker bio'], field: 'speaker_bio' },
  { patterns: ['speaker title', 'role', 'job title'], field: 'speaker_title' },
  { patterns: ['company', 'organization', 'org', 'school'], field: 'speaker_company' },
  { patterns: ['speaker email', 'email'], field: 'speaker_email' },
];

/**
 * Attempt to auto-detect a field from a column header string.
 * Returns the field value string or null.
 */
function autoDetectField(header) {
  if (!header) return null;
  const normalized = header.trim().toLowerCase();
  for (const rule of AUTO_DETECT_RULES) {
    if (rule.patterns.includes(normalized)) {
      return rule.field;
    }
  }
  return null;
}

/**
 * Get sample values for a column from preview rows.
 * Returns up to `count` non-empty values, truncated.
 */
function getSampleValues(previewRows, colIndex, count = 3, maxLen = 50) {
  const samples = [];
  if (!previewRows) return samples;
  for (let i = 0; i < previewRows.length && samples.length < count; i++) {
    const val = previewRows[i]?.[colIndex];
    if (val != null && String(val).trim() !== '') {
      const s = String(val).trim();
      samples.push(s.length > maxLen ? s.slice(0, maxLen) + '...' : s);
    }
  }
  return samples;
}

/**
 * ColumnMapper -- maps spreadsheet columns to LacSched fields.
 *
 * Props:
 *   headers: string[]              -- spreadsheet column headers
 *   previewRows: string[][]        -- sample data rows
 *   onChange(mapping)               -- called with { colIndex: fieldName } whenever mapping changes
 *   initialMapping?: object         -- pre-existing mapping to load for editing
 */
export default function ColumnMapper({ headers, previewRows, onChange, initialMapping }) {
  // mapping: { [colIndex]: fieldValue | '' }  ('' means skip)
  const [mapping, setMapping] = useState({});

  // Build initial mapping on mount / when headers change
  useEffect(() => {
    if (!headers || headers.length === 0) return;

    if (initialMapping && Object.keys(initialMapping).length > 0) {
      // Use provided initial mapping
      setMapping(initialMapping);
      return;
    }

    // Auto-detect
    const detected = {};
    const usedFields = new Set();
    for (let i = 0; i < headers.length; i++) {
      const field = autoDetectField(headers[i]);
      if (field && !usedFields.has(field)) {
        detected[i] = field;
        usedFields.add(field);
      }
    }
    setMapping(detected);
  }, [headers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent whenever mapping changes
  useEffect(() => {
    if (onChange) {
      // Only send non-empty mappings
      const clean = {};
      for (const [k, v] of Object.entries(mapping)) {
        if (v) clean[k] = v;
      }
      onChange(clean);
    }
  }, [mapping]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute which fields are already used (for duplicate detection)
  const usedFields = useMemo(() => {
    const used = {};
    for (const [colIdx, field] of Object.entries(mapping)) {
      if (field) {
        if (!used[field]) used[field] = [];
        used[field].push(Number(colIdx));
      }
    }
    return used;
  }, [mapping]);

  // Validation
  const validation = useMemo(() => {
    const errors = [];
    const warnings = [];

    // Required: title must be mapped
    const titleMapped = Object.values(mapping).includes('title');
    if (!titleMapped) {
      errors.push('The "Title" field is required and must be mapped to a column.');
    }

    // Start/end time pairing
    const hasStart = Object.values(mapping).includes('start_time');
    const hasEnd = Object.values(mapping).includes('end_time');
    if (hasStart && !hasEnd) {
      warnings.push('Start Time is mapped but End Time is not. Both are recommended for scheduling.');
    }
    if (!hasStart && hasEnd) {
      warnings.push('End Time is mapped but Start Time is not. Both are recommended for scheduling.');
    }

    // Duplicate check
    for (const [field, cols] of Object.entries(usedFields)) {
      if (cols.length > 1) {
        const fieldLabel = FIELD_GROUPS.flatMap(g => g.fields).find(f => f.value === field)?.label || field;
        errors.push(`"${fieldLabel}" is mapped to multiple columns. Each field can only be mapped once.`);
      }
    }

    return { errors, warnings, isValid: errors.length === 0 };
  }, [mapping, usedFields]);

  const handleChange = (colIndex, fieldValue) => {
    setMapping(prev => {
      const next = { ...prev };
      if (fieldValue === '') {
        delete next[colIndex];
      } else {
        next[colIndex] = fieldValue;
      }
      return next;
    });
  };

  // Check if a field is already used by another column
  const isFieldUsedByOther = (field, currentColIndex) => {
    if (!field) return false;
    const cols = usedFields[field];
    if (!cols) return false;
    return cols.some(c => c !== currentColIndex);
  };

  if (!headers || headers.length === 0) {
    return (
      <div style={{ padding: '1rem', color: 'var(--si-text-muted)', textAlign: 'center' }}>
        No columns to map. Please select a sheet with data first.
      </div>
    );
  }

  return (
    <div>
      {/* Column mapping rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem',
          padding: '0.5rem 0.75rem',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--si-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          <div>Spreadsheet Column</div>
          <div>Map To</div>
        </div>

        {headers.map((header, colIndex) => {
          const currentField = mapping[colIndex] || '';
          const samples = getSampleValues(previewRows, colIndex);
          const isDuplicate = currentField && isFieldUsedByOther(currentField, colIndex);
          const isTitleField = currentField === 'title';

          return (
            <div
              key={colIndex}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                alignItems: 'center',
                padding: '0.75rem',
                background: currentField ? 'var(--si-accent-muted)' : 'var(--si-bg-surface)',
                borderRadius: '6px',
                border: `1px solid ${isDuplicate ? 'var(--si-error-border)' : currentField ? 'var(--si-accent)' : 'var(--si-border-strong)'}`,
                transition: 'all 0.2s',
              }}
            >
              {/* Left: Column header + sample data */}
              <div>
                <div style={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'var(--si-text-primary)',
                }}>
                  {header || `Column ${colIndex + 1}`}
                </div>
                {samples.length > 0 && (
                  <div style={{
                    marginTop: '0.25rem',
                    fontSize: '0.75rem',
                    color: 'var(--si-text-muted)',
                    lineHeight: 1.4,
                  }}>
                    {samples.map((s, i) => (
                      <span key={i}>
                        {i > 0 && <span style={{ color: 'var(--si-btn-disabled)' }}> | </span>}
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Dropdown */}
              <div>
                <select
                  value={currentField}
                  onChange={(e) => handleChange(colIndex, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.8125rem',
                    color: currentField ? 'var(--si-text-primary)' : 'var(--si-text-muted)',
                    background: 'var(--si-bg-deep)',
                    border: `1px solid ${isDuplicate ? 'var(--si-error)' : 'var(--si-border-strong)'}`,
                    borderRadius: '6px',
                    outline: 'none',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    appearance: 'auto',
                  }}
                >
                  <option value="">-- Skip this column --</option>
                  {FIELD_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.fields.map((field) => {
                        const usedByOther = isFieldUsedByOther(field.value, colIndex);
                        return (
                          <option
                            key={field.value}
                            value={field.value}
                            disabled={usedByOther}
                          >
                            {field.label}{field.required ? ' *' : ''}{usedByOther ? ' (already mapped)' : ''}
                          </option>
                        );
                      })}
                    </optgroup>
                  ))}
                </select>
                {isDuplicate && (
                  <div style={{
                    marginTop: '0.25rem',
                    fontSize: '0.6875rem',
                    color: 'var(--si-error)',
                  }}>
                    This field is mapped to another column
                  </div>
                )}
                {!isDuplicate && currentField && (() => {
                  const fieldDef = FIELD_GROUPS.flatMap(g => g.fields).find(f => f.value === currentField);
                  return fieldDef?.hint ? (
                    <div style={{
                      marginTop: '0.25rem',
                      fontSize: '0.6875rem',
                      color: 'var(--si-text-secondary)',
                      fontStyle: 'italic',
                    }}>
                      {fieldDef.hint}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Validation Summary */}
      <div style={{ marginTop: '1rem' }}>
        {/* Mapped fields summary */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.625rem 0.75rem',
          fontSize: '0.8125rem',
          color: 'var(--si-text-secondary)',
          background: 'var(--si-bg-surface)',
          borderRadius: '6px 6px 0 0',
          borderBottom: '1px solid var(--si-border-strong)',
        }}>
          <span>
            {Object.values(mapping).filter(v => v).length} of {headers.length} columns mapped
          </span>
          {validation.isValid && (
            <svg style={{ width: 14, height: 14, color: 'var(--si-success)', marginLeft: 'auto' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          )}
        </div>

        {/* Errors */}
        {validation.errors.length > 0 && (
          <div style={{
            padding: '0.625rem 0.75rem',
            background: 'var(--si-error-muted)',
            borderLeft: '3px solid var(--si-error)',
          }}>
            {validation.errors.map((err, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                fontSize: '0.8125rem',
                color: 'var(--si-error)',
                marginBottom: i < validation.errors.length - 1 ? '0.375rem' : 0,
              }}>
                <svg style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <span>{err}</span>
              </div>
            ))}
          </div>
        )}

        {/* Warnings */}
        {validation.warnings.length > 0 && (
          <div style={{
            padding: '0.625rem 0.75rem',
            background: 'var(--si-accent-muted)',
            borderLeft: '3px solid var(--si-accent)',
          }}>
            {validation.warnings.map((warn, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                fontSize: '0.8125rem',
                color: 'var(--si-accent)',
                marginBottom: i < validation.warnings.length - 1 ? '0.375rem' : 0,
              }}>
                <svg style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span>{warn}</span>
              </div>
            ))}
          </div>
        )}

        {/* Valid message */}
        {validation.isValid && validation.warnings.length === 0 && (
          <div style={{
            padding: '0.625rem 0.75rem',
            background: 'var(--si-success-muted)',
            borderLeft: '3px solid var(--si-success)',
            borderRadius: '0 0 6px 6px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.8125rem',
              color: 'var(--si-success)',
            }}>
              <svg style={{ width: 14, height: 14, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Mapping looks good. Ready to import.</span>
            </div>
          </div>
        )}

        {/* Valid with warnings */}
        {validation.isValid && validation.warnings.length > 0 && (
          <div style={{
            padding: '0.625rem 0.75rem',
            background: 'var(--si-accent-muted)',
            borderRadius: '0 0 6px 6px',
            borderTop: '1px solid var(--si-accent)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.8125rem',
              color: 'var(--si-text-secondary)',
            }}>
              <svg style={{ width: 14, height: 14, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Mapping valid with warnings. You can still import.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Export validation helper so parent can check validity
ColumnMapper.FIELD_GROUPS = FIELD_GROUPS;
